import { Link } from "@remix-run/react";
import type { SolutionSeoContent } from "~/utils/seo-solution-content";
import { SOLUTION_SEO_CONFIG } from "~/utils/seo-solution-content";
import { seoPageFontClass } from "~/utils/seo-game-content";
import { getBreadcrumbListJsonLd, getFaqPageJsonLd } from "~/utils/seo-meta";
import { seoRouteLabel } from "~/utils/seo-routes";
import leftQuoteIcon from "~/assets/icons/left-quote.svg";

const fontClass = seoPageFontClass;

interface SolutionSeoPageProps {
  content: SolutionSeoContent;
}

function solutionSlugToTitle(slug: string): string {
  const config = SOLUTION_SEO_CONFIG[slug];
  return config?.headline ?? slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function SolutionSeoPage({ content }: SolutionSeoPageProps) {
  const { slug, headline, problem, longDescription, stats, howPrizoSolves, testimonial, faq, relatedSolutions } = content;
  const pagePath = `/solutions/${slug}`;

  return (
    <div className="bg-[#ffffff] w-full">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbListJsonLd([
              { name: "Home", path: "/" },
              { name: seoRouteLabel(pagePath) ?? headline, path: pagePath },
            ]),
          ),
        }}
      />
      {faq?.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getFaqPageJsonLd(faq, pagePath)),
          }}
        />
      )}
      {/* Hero */}
      <div className="seo-section">
        <div className="seo-hero-head">
          <h1 className="seo-h1">
            {headline}
          </h1>
          <p className="seo-lead">
            {problem}
          </p>
        </div>
        <div className="seo-cta-row">
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-primary"
          >
            Try For Free
          </a>
          <a
            href="https://calendly.com/nadav-prizo/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-secondary"
          >
            Book a Demo
          </a>
        </div>
      </div>

      {/* Long description */}
      {longDescription && (
        <div className="bg-[#F9F9FB] w-full py-12 md:py-16">
          <div className="max-w-[720px] mx-auto px-4 md:px-[120px]">
            <div className="seo-body leading-[1.75] space-y-4">
              {longDescription.split(/\n\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="seo-band-violet">
        <div className="seo-wrap">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[900px] mx-auto text-center">
            {stats.map((stat, i) => (
              <div key={i}>
                <div className={`${fontClass} font-bold text-[#696cff] text-[32px] md:text-[40px] mb-2`}>
                  {stat.value}
                </div>
                <div className={`${fontClass} text-[#6a6a6a] text-[14px] md:text-[16px]`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How Prizo Solves This */}
      <div className="seo-section">
        <h2 className="seo-h2">
          How Prizo Solves This
        </h2>
        <ul className="seo-list">
          {howPrizoSolves.map((item, i) => (
            <li key={i} className="seo-li">
              <span className="text-[#696cff] mt-1">✓</span>
              <span className="seo-body">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Testimonial */}
      <div className="bg-[rgba(105,108,255,0.06)] w-full py-12 md:py-16">
        <div className="seo-wrap">
          <div className="max-w-[720px] mx-auto bg-[#F4F4FF] rounded-2xl p-6 md:p-8">
            <img src={leftQuoteIcon} alt="" className="w-10 h-10 mb-4" />
            <p className={`${fontClass} text-[#6A6A6A] text-[15px] md:text-[16px] leading-[1.6] italic`}>
              "{testimonial}"
            </p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      {faq?.length > 0 && (
        <div className="seo-section">
          <h2 className="seo-h2">
            Frequently Asked Questions
          </h2>
          <div className="seo-faq-list">
            {faq.map((item, i) => (
              <div key={i} className="seo-faq-item">
                <h3 className="seo-h3">
                  {item.question}
                </h3>
                <p className="seo-muted">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related solutions */}
      {relatedSolutions?.length > 0 && (
        <div className="seo-band-violet">
          <div className="seo-wrap">
            <h2 className="seo-h2">
              Related Solutions
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {relatedSolutions.map((slug) => (
                <Link
                  key={slug}
                  to={`/solutions/${slug}`}
                  className="seo-pill"
                >
                  {solutionSlugToTitle(slug)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="seo-wrap py-12 md:py-16 text-center">
        <a
          href="https://apps.shopify.com/prizo"
          target="_blank"
          rel="noopener noreferrer"
          className="seo-btn-cta"
        >
          Install on Shopify — Free Trial
        </a>
      </div>
    </div>
  );
}
